import { api } from '@/lib/api/client';
import type {
  SmsGatewayResponse, SmsGatewaySettings, TestConnectionResult,
  SmsTemplate, SmsTemplateInput, TemplatePreview, TestSendResult,
  DeliveryLogRow, DeliveryLogFilters, AutomaticEvent,
} from './types';

/** GET /api/admin/sms-gateway/settings — the account and its readiness verdict together. */
export const fetchGateway = () => api.get<SmsGatewayResponse>('/admin/sms-gateway/settings');

export const saveGateway = (data: Partial<SmsGatewaySettings> & { auth_key?: string }) =>
  api.patch<SmsGatewayResponse>('/admin/sms-gateway/settings', data);

/**
 * Save & test. The save is deliberately part of this call: testing the
 * previously-stored key while the admin looks at a newly pasted one reports on
 * a state nobody is asking about, and reads as the new key being rejected.
 */
export const testConnection = (data: Partial<SmsGatewaySettings> & { auth_key?: string }) =>
  api.post<TestConnectionResult>('/admin/sms-gateway/settings/test', data);

export const fetchTemplates = () => api.get<SmsTemplate[]>('/admin/sms-gateway/templates');

export const createTemplate = (data: SmsTemplateInput) =>
  api.post<SmsTemplate>('/admin/sms-gateway/templates', data);

export const updateTemplate = (id: number, data: SmsTemplateInput) =>
  api.patch<SmsTemplate>(`/admin/sms-gateway/templates/${id}`, data);

/** Switches a wording off rather than deleting it — old log rows still refer to it. */
export const deactivateTemplate = (id: number) =>
  api.delete<SmsTemplate>(`/admin/sms-gateway/templates/${id}`);

/** Builds and costs the message without sending it or touching a credential. */
export const previewTemplate = (id: number, values: string[]) =>
  api.post<TemplatePreview>(`/admin/sms-gateway/templates/${id}/preview`, { values });

export const testSend = (id: number, mobile: string, values: string[]) =>
  api.post<TestSendResult>(`/admin/sms-gateway/templates/${id}/test-send`, { mobile, values });

/** Every message the app sends by itself — the Notifications tab's rows. */
export const fetchEvents = () => api.get<AutomaticEvent[]>('/admin/sms-gateway/events');

export const fetchDeliveryLog = (filters: DeliveryLogFilters) =>
  api.get<DeliveryLogRow[]>('/admin/sms-gateway/log', { query: filters });
